import { ContractSuretyType, Dtos, SuretyType } from "@inrev/common";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { DateTime } from "luxon";
import { match } from "ts-pattern";

export type ContractUpdateForm = Dtos.Admin.SuretyQuote.Contract.Update.Request;

const BidUpdateForm = Dtos.Admin.SuretyQuote.Contract.Bid.Update.Request.schema;
const FinalUpdateForm = Dtos.Admin.SuretyQuote.Contract.Final.Update.Request.schema;
const BidToFinalUpdateForm = Dtos.Admin.SuretyQuote.Contract.BidToFinal.Update.Request.schema;

export const getContractUpdateSchema = (contractSuretyType: ContractSuretyType) =>
	match(contractSuretyType)
		.with(ContractSuretyType.bid, () => BidUpdateForm)
		.with(ContractSuretyType.final, () => FinalUpdateForm)
		.with(ContractSuretyType.bid_to_final, () => BidToFinalUpdateForm)
		.exhaustive();

export const mapAdminBondRequestToContractUpdateForm = (
	r: AdminBondRequest,
): ContractUpdateForm => {
	const base = {
		suretyType: SuretyType.contract,
		updatedAt: DateTime.fromISO(r.updatedAt),
		bondAmount: r.bondAmount,
		tListingRequired: r.tListingRequired,
		completionBond: r.completionBond,
		sfaaCodeId: r.sfaaCode?.id ?? null,
		contractWarrantyMonths: r.project.contractWarrantyMonths,
		project: {
			...r.project,
			scopeOfWork: (r.project.scopeOfWork ?? []).map((row) => row.id),
			city: r.project.address?.city,
			state: r.project.address?.state,
		},
	};

	const discriminatedData = match(r)
		.with({ contractSuretyType: ContractSuretyType.bid }, (r) => ({
			contractSuretyType: ContractSuretyType.bid,
			bidAmount: r.bidAmount,
			bidBondAmountType: r.bidBondAmountType,
			bondAmountPercentOfBid: r.bondAmountPercentOfBid,
			bidDate: r.bidDate,
			finalBondFormType: r.finalBondFormType,
		}))
		.with({ contractSuretyType: ContractSuretyType.final }, (r) => ({
			contractSuretyType: ContractSuretyType.final,
			contractAmount: r.contractAmount,
		}))
		.with({ contractSuretyType: ContractSuretyType.bid_to_final }, (r) => ({
			contractSuretyType: ContractSuretyType.bid_to_final,
			contractAmount: r.contractAmount,
			actualBidAmount: r.actualBidAmount,
			secondBid: r.secondBid,
			actualBidDate: r.actualBidDate ? DateTime.fromISO(r.actualBidDate) : null,
			contractDate: r.contractDate ? DateTime.fromISO(r.contractDate) : null,
		}))
		.exhaustive();

	return {
		...base,
		...discriminatedData,
	};
};
